import { useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { UseFloatingReturn } from '@floating-ui/react';
import { CallIntelTypes, findCitationInTranscript } from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ChipVariants, Text, useTooltip } from '@frontend/design-system';
import { variantBackgroundMap } from '../constants';
import { callIntelUtils } from '../utils/call-intel-utils';

type Citation = { popoverLabel: string; text: string; time?: string; variant: ChipVariants };

type HighlightCitationsParams = {
  citations: Citation[];
  time: string;
  timeDataMapping?: CallIntelTypes.TranscriptTimeDataMapping;
  transcript: string;
};

type Props = {
  caller?: CallIntelTypes.Person;
  citations?: Citation[];
  highlightedCitation?: CallIntelTypes.HighlightedCitation;
  officeUser?: CallIntelTypes.OfficeUser | null;
  speaker: string;
  text: string;
  time: string;
  timeDataMapping?: CallIntelTypes.TranscriptTimeDataMapping;
};

const extractBackground = (variant: ChipVariants): string => variantBackgroundMap[variant] ?? 'transparent';

const highlightCitations = (
  { transcript, citations, time, timeDataMapping }: HighlightCitationsParams,
  ref: UseFloatingReturn['refs']['setReference']
): JSX.Element[] => {
  const processedTexts = new Set<string>();
  const elements: JSX.Element[] = [];

  let lastIndex = 0;

  citations.forEach(({ popoverLabel, text, variant, time }) => {
    let isSubset = false;
    for (const processedText of processedTexts) {
      if (findCitationInTranscript({ citation: text, transcript: processedText }).isFound) {
        isSubset = true;
        break;
      }
    }

    if (!isSubset) {
      const {
        start: startIndex,
        end: endIndex,
        isFound,
      } = findCitationInTranscript({
        citation: text,
        transcript: transcript.slice(lastIndex),
      });
      if (isFound) {
        // Push text before highlight
        if (startIndex > lastIndex) {
          elements.push(<span key={`text-${lastIndex}`}>{transcript.slice(lastIndex, startIndex)}</span>);
        }

        // Push the highlighted text
        elements.push(
          <span
            key={`highlight-${startIndex}`}
            className='highlight'
            style={{ backgroundColor: extractBackground(variant) }}
            data-citation={`citation-${time}`}
            data-popover-label={popoverLabel ?? ''}
            ref={ref}
          >
            {text}
          </span>
        );

        lastIndex = endIndex;
        processedTexts.add(text);
      }
    }
  });

  // Push remaining text
  if (lastIndex < transcript.length) {
    elements.push(<span key={`text-${lastIndex}`}>{transcript.slice(lastIndex)}</span>);
  }

  if (timeDataMapping && timeDataMapping[time]) {
    const { hightlightColor, popoverLabel } = timeDataMapping[time];

    return [
      <span
        key={`time-highlight-${time}`}
        className='highlight'
        style={{ backgroundColor: hightlightColor }}
        data-citation={`citation-${time}`}
        data-popover-label={popoverLabel ?? ''}
        ref={ref}
      >
        {elements}
      </span>,
    ];
  }

  return elements;
};

export const TranscriptCard = ({
  caller,
  citations = [],
  highlightedCitation,
  officeUser,
  speaker,
  text,
  time,
  timeDataMapping,
}: Props) => {
  const { t } = useTranslation('call-intelligence');
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });

  const isOfficeUserInactive = speaker === 'office' && callIntelUtils.isUserInactive(officeUser);

  const speakerName = useMemo(() => {
    if (speaker === 'ai_receptionist') return t('AI Receptionist');
    if (speaker === 'office') return `${officeUser?.firstName || ''} ${officeUser?.lastName || ''}`.trim() || speaker;
    return `${caller?.firstName || ''} ${caller?.lastName || ''}`.trim() || speaker;
  }, [speaker, officeUser?.firstName, officeUser?.lastName, caller?.firstName, caller?.lastName, t]);

  const content = highlightCitations({ citations, time, timeDataMapping, transcript: text }, triggerProps.ref);

  const highlightRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (highlightedCitation?.time && highlightRef.current) {
      const highlightedElement = highlightRef.current.querySelector(
        `[data-citation="citation-${highlightedCitation.time}"]`
      );

      if (highlightedElement) {
        setTimeout(() => {
          highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    }
  }, [highlightedCitation?.time, citations]);

  const popoverLabel = Array.from(highlightRef.current?.querySelectorAll(`[data-popover-label]`) || [])
    .map((el) => el.getAttribute('data-popover-label'))
    .filter(Boolean) // Remove any null values
    .join(', ');

  return (
    <li css={styles.wrapper}>
      <Text className='time' weight='bold' size='medium'>
        {time}
      </Text>

      <Text className='transcript' size='medium'>
        <strong style={{ color: isOfficeUserInactive ? theme.colors.neutral50 : 'inherit' }}>{speakerName}</strong>:{' '}
        <span ref={highlightRef}>
          <span>{content}</span>
        </span>
      </Text>

      {popoverLabel && (
        <Tooltip {...tooltipProps}>
          <Text size='medium' color='white'>
            {popoverLabel}
          </Text>
        </Tooltip>
      )}
    </li>
  );
};

const styles = {
  wrapper: css`
    column-gap: ${theme.spacing(1)};
    display: flex;

    &:not(:last-of-type) {
      margin-bottom: ${theme.spacing(2)};
    }

    .time {
      color: ${theme.colors.neutral30};
      flex-basis: ${theme.spacing(7)};
    }

    .transcript {
      flex: 1;

      strong {
        text-transform: capitalize;
      }
    }

    .highlight {
      cursor: pointer;
    }
  `,
};
